import React, { HTMLAttributes, PropsWithChildren } from 'react'

import classNames from 'classnames'

import { STATUSES, TEXT_SHADES } from 'hsds/utils/constants'
import { getValidProps } from 'hsds/utils/react'

import { TextUI } from './Text.styles'

export const WEIGHTS = [900, 800, 700, 600, 500, 400, 300, 200, 100] as const
export const SIZES = [10, 11, 12, 13, 14, 15, 20, 32, 48] as const

export type TextSizes = (typeof SIZES)[number] | `${(typeof SIZES)[number]}`
export type TextWeights =
  | (typeof WEIGHTS)[number]
  | `${(typeof WEIGHTS)[number]}`

type Props = {
  allCaps?: boolean
  block?: boolean
  /** Data attr for Cypress tests. */
  'data-cy'?: string
  isPlainLink?: boolean
  noUnderline?: boolean
  selector?: string
  /** Custom class names to be added to the component. */
  className?: string
  /** Center aligns text. */
  center?: boolean
  /** Disables text selection. */
  disableSelect?: boolean
  /** Changes text color to a very light grey. */
  faint?: boolean
  /** Applies [Link](../Link) styles. */
  linkStyle?: boolean
  /** Inherit the line-height from a parent selector. */
  lineHeightInherit?: boolean
  /** Resets the line-height to `1`. */
  lineHeightReset?: boolean
  /** Changes text color to a light grey. */
  muted?: boolean
  /** Prevents text from wrapping. */
  noWrap?: boolean
  /** Changes text color shade. */
  shade?: (typeof TEXT_SHADES)[number] | null
  /** Adjust text size. */
  size?: TextSizes
  /** Changes the text color based on state. */
  state?: (typeof STATUSES)[number] | null
  /** Changes text color to a lighter grey. */
  subtle?: boolean
  /** Enables CSS truncation for text. */
  truncate?: boolean
  /** Adjust text weight. */
  weight?: TextWeights
  /** Enables CSS `word-break` for text. */
  wordWrap?: boolean
  as?: string
} & HTMLAttributes<HTMLElement>

const Text = (props: PropsWithChildren<Props>) => {
  const {
    allCaps,
    block,
    children,
    className,
    center,
    disableSelect,
    faint,
    isPlainLink,
    lineHeightInherit,
    lineHeightReset,
    linkStyle,
    muted,
    noUnderline,
    noWrap,
    selector,
    shade,
    size,
    state,
    subtle,
    truncate,
    weight,
    wordWrap,
    ...rest
  } = props

  return (
    <TextUI
      as={selector}
      {...getValidProps(rest)}
      className={classNames(
        'c-Text',
        allCaps && 'is-all-caps',
        block && 'is-block',
        center && 'is-center',
        disableSelect && 'is-disableSelect',
        faint && 'is-faint is-shade-faint',
        isPlainLink && 'is-plainLink',
        muted && 'is-muted is-shade-muted',
        noUnderline && 'is-noUnderline',
        noWrap && 'is-no-wrap',
        lineHeightInherit && 'is-lineHeightInherit',
        lineHeightReset && 'is-lineHeightReset',
        linkStyle && 'is-linkStyle',
        selector && `is-${selector}`,
        shade && `is-shade-${shade}`,
        size && `is-${size}`,
        state && `is-${state}`,
        subtle && 'is-subtle is-shade-subtle',
        truncate && 'is-truncate',
        weight && `is-${weight}`,
        wordWrap && 'is-wordWrap',
        className
      )}
    >
      {children}
    </TextUI>
  )
}

Text.defaultProps = {
  center: false,
  'data-cy': 'Text',
  disableSelect: false,
  isPlainLink: false,
  linkStyle: false,
  noUnderline: false,
  selector: 'span',
  size: '13',
  state: 'default',
  truncate: false,
}

export default Text
